import React from 'react';
import UserAvatar from '../../../../../shared/components/general/UserAvatar';
import { css } from '@emotion/css';
import {
    color_colors_growth,
    color_colors_growth_border,
    color_colors_growth_light,
    color_defaults_white,
    color_shades_dark,
    color_shades_darker,
    color_shades_darkest,
    color_shades_light,
    voicify_spark_lightest,
    voicify_spark_dark,
    voicify_spark_darker,
    voicify_spark_medium
} from '../../../../../shared/constants/colors';

export interface PlatformOrganizationItemModel {
    id: string;
    name: string;
    imageUri?: string | null;
    isMultiLingual?: boolean;
    hasLiveApp: boolean;
    liveAppCount: number;
    matchingAppCount: number;
    matchingAppIds: string[];
}

interface PlatformOrganizationItemProps {
    organization: PlatformOrganizationItemModel;
    isSelected: boolean;
    disabled?: boolean;
    onToggleFilter: () => void;
}

const PlatformOrganizationItem: React.FC<PlatformOrganizationItemProps> = ({
    organization,
    isSelected,
    disabled = false,
    onToggleFilter
}) => {
    return (
        <div
            className={`${itemStyle} ${isSelected ? selectedStyle : ''} ${disabled ? disabledStyle : ''}`}
            onClick={disabled ? undefined : onToggleFilter}
            role="button"
            tabIndex={disabled ? -1 : 0}
            onKeyDown={event => {
                if (!disabled && (event.key === 'Enter' || event.key === ' ')) {
                    event.preventDefault();
                    onToggleFilter();
                }
            }}
            aria-pressed={isSelected}
        >
            <div
                className={checkboxWrapperStyle}
                onClick={event => {
                    event.preventDefault();
                    event.stopPropagation();
                    if (!disabled) {
                        onToggleFilter();
                    }
                }}
            >
                <input
                    className={checkboxStyle}
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => onToggleFilter()}
                    disabled={disabled}
                    onClick={event => event.stopPropagation()}
                    aria-label={`Toggle filter for ${organization.name}`}
                />
            </div>
            <UserAvatar
                firstName={organization.name || 'O'}
                lastName=""
                src={organization.imageUri}
                size={36}
                noShadow
                noImageText={organization.name?.split(' ').map(word => word[0]).join('').slice(0, 3).toUpperCase() || 'ORG'}
            />
            <div className={textContainerStyle}>
                <div className={nameRowStyle}>
                    <span className={nameStyle}>{organization.name}</span>
                    {organization.isMultiLingual && <span className={badgeStyle}>ES</span>}
                </div>
                <span className={metaStyle}>{organization.matchingAppCount} assistants</span>
            </div>
            <div className={statusPillRowStyle}>
                <div className={`${liveStatusPillStyle} ${organization.hasLiveApp ? liveStatusPillOnStyle : liveStatusPillOffStyle}`}>
                    {organization.hasLiveApp ? `${organization.liveAppCount} Live` : 'Not Live'}
                </div>
            </div>
        </div>
    );
};

const itemStyle = css`
    width: 100%;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px;
    border: 1px solid ${color_shades_light};
    border-radius: 12px;
    background: ${color_defaults_white};
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 2px 4px rgba(50, 70, 97, 0.08);
    position: relative;
    overflow: hidden;
    opacity: 0.4;

    &:hover {
        opacity: 0.6;
        border-color: ${voicify_spark_medium};
        box-shadow: 0 4px 12px rgba(30, 126, 185, 0.15);
        transform: translateY(-2px);
    }

    &:focus-visible {
        outline: none;
        border-color: ${voicify_spark_medium};
        box-shadow: 0 0 0 2px ${voicify_spark_lightest};
    }
`;

const selectedStyle = css`
    opacity: 1;
`;

const disabledStyle = css`
    opacity: 0.55;
    cursor: not-allowed;

    &:hover {
        opacity: 0.55;
        transform: none;
        border-color: ${color_shades_light};
        box-shadow: 0 2px 4px rgba(50, 70, 97, 0.08);
    }
`;

const textContainerStyle = css`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
    text-align: left;
`;

const checkboxWrapperStyle = css`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 8px 0 4px;
    flex-shrink: 0;
    cursor: pointer;
    z-index: 1;
`;

const checkboxStyle = css`
    width: 16px;
    height: 16px;
    cursor: pointer;
    appearance: none;
    border: 2px solid ${color_shades_light};
    border-radius: 3px;
    background: ${color_defaults_white};
    position: relative;
    transition: all 0.2s;

    &:checked {
        background: ${voicify_spark_darker};
        border-color: ${voicify_spark_darker};
    }

    &:checked::after {
        content: '';
        position: absolute;
        left: 50%;
        top: 45%;
        width: 3px;
        height: 7px;
        border: solid white;
        border-width: 0 2px 2px 0;
        transform: translate(-50%, -50%) rotate(45deg);
    }

    &:hover:not(:disabled) {
        transform: scale(1.1);
        border-color: ${voicify_spark_medium};
    }
`;

const nameRowStyle = css`
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
`;

const nameStyle = css`
    font-family: Muli, sans-serif;
    font-size: 11px;
    font-weight: 600;
    color: ${color_shades_darkest};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const metaStyle = css`
    font-family: Muli, sans-serif;
    font-size: 9px;
    font-weight: 500;
    color: ${color_shades_darker};
`;

const statusPillRowStyle = css`
    display: flex;
    align-items: center;
    flex-shrink: 0;
`;

const liveStatusPillStyle = css`
    padding: 4px 10px;
    border-radius: 16px;
    font-family: Muli, sans-serif;
    font-size: 10px;
    font-weight: 600;
    white-space: nowrap;
    border: 1px solid ${color_shades_light};
`;

const liveStatusPillOnStyle = css`
    background: ${color_colors_growth_light};
    color: ${color_colors_growth};
    border-color: ${color_colors_growth_border};
`;

const liveStatusPillOffStyle = css`
    background: ${color_shades_light};
    color: ${color_shades_darker};
`;

const badgeStyle = css`
    padding: 2px 6px;
    border-radius: 999px;
    font-family: Muli, sans-serif;
    font-size: 9px;
    font-weight: 800;
    background: rgba(28, 102, 151, 0.12);
    color: ${voicify_spark_dark};
`;

export default PlatformOrganizationItem;
